import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import {
  selectCanUseGrow,
  selectClinicId,
  selectGrowIntegration,
  selectIsTrialExpired,
} from "../../store/slices/authSlice";
import { useCreateGrowPaymentLinkMutation, useSendGrowPaymentLinkMutation } from "../../store/services/growApi";
import { useToast } from "../../contexts/ToastContext";
import HoverTip from "../ui/HoverTip";
import ConfirmDialog from "../ui/ConfirmDialog";
import LoaderLoop from "../shared/loader/LoaderLoop";
import { copyGrowLink } from "./GrowLinkPopover";
import "./GrowLinks.css";

const LINKABLE = ["expected", "pending", "pending_collection"];

/**
 * Why a "request payment" button is disabled, or null when allowed.
 * Exported so other surfaces (patient tab header) can reuse the rules.
 */
export const getRequestPaymentBlocker = ({ transactions = [], patient, trialExpired }) => {
  if (trialExpired) return "תקופת הניסיון הסתיימה";
  if (!transactions.length) return "לא נבחרו תשלומים";
  if (transactions.some((t) => !LINKABLE.includes(t.status))) return "ניתן ליצור דרישת תשלום רק על תשלומים שטרם שולמו";
  const patientIds = new Set(transactions.map((t) => t.patientId || null));
  if (patientIds.has(null)) return "התשלום אינו משויך למטופל";
  if (patientIds.size !== 1) return "כל התשלומים חייבים להיות של אותו מטופל";
  const sum = transactions.reduce((a, t) => a + (Number(t.amount) || 0), 0);
  if (sum <= 0) return "הסכום חייב להיות גדול מאפס";
  if (patient && !patient.phone) return "למטופל אין מספר טלפון";
  if (patient && (!patient.firstName || !patient.lastName)) return "ל-Grow נדרשים שם פרטי ושם משפחה של המטופל";
  return null;
};

/**
 * RequestPaymentButton — creates a Grow payment link for 1..n transactions of
 * one patient. Renders NOTHING when the user cannot use Grow
 * (`selectCanUseGrow`: integrated + plan + permission). Disabled with a
 * tooltip for the guard rails in getRequestPaymentBlocker. On success the URL
 * is copied to the clipboard and `onCreated({linkId,url,sum,reused})` fires so
 * the host can open the popover / send flow.
 *
 * Props: transactions, patient?, activeLink? (if given → renders null; host shows the chip),
 *        label?, size? ("sm"|"md"), variant? ("primary"|"secondary"|"ghost"), stopPropagation?, onCreated?
 * Error code: CK-3871
 */
export default function RequestPaymentButton({
  transactions = [],
  patient = null,
  activeLink = null,
  label = "דרישת תשלום",
  size = "sm",
  variant = "secondary",
  stopPropagation = true,
  className = "",
  onCreated,
  confirm = true,
  hideWhenUnassigned = true,
}) {
  const canUseGrow = useSelector(selectCanUseGrow);
  const trialExpired = useSelector(selectIsTrialExpired);
  const clinicId = useSelector(selectClinicId);
  const { showSuccess, showError } = useToast();
  const [createLink, { isLoading }] = useCreateGrowPaymentLinkMutation();
  const [sendLink, { isLoading: isSending }] = useSendGrowPaymentLinkMutation();
  const growSettings = useSelector(selectGrowIntegration);
  const sendImmediately = growSettings?.sendImmediately !== false;
  const [justCreated, setJustCreated] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const blocker = useMemo(
    () => getRequestPaymentBlocker({ transactions, patient, trialExpired }),
    [transactions, patient, trialExpired]
  );

  if (!canUseGrow || activeLink) return null;
  // Unassigned ("ללא שיוך") rows: a disabled button on every card is clutter — render nothing.
  if (hideWhenUnassigned && transactions.some((t) => !t.patientId)) return null;

  const sum = transactions.reduce((a, t) => a + (Number(t.amount) || 0), 0);
  const patientName = transactions[0]?.patientName || (patient ? `${patient.firstName || ""} ${patient.lastName || ""}`.trim() : "");

  const create = async () => {
    setConfirming(false);
    const res = await createLink({ clinicId, transactionIds: transactions.map((t) => t.id) });
    if (res.error) {
      console.error("[CK-3871] Create Grow payment link failed", {
        errorCode: "CK-3871",
        errorMessage: res.error.message,
        growErrorId: res.error.details?.growErrorId ?? null,
        count: transactions.length,
        timestamp: new Date().toISOString(),
      });
      showError(res.error.message);
      return;
    }
    const data = res.data || {};
    const copied = data.url ? await copyGrowLink(data.url) : false;
    let sentMsg = "";
    if (sendImmediately && data.linkId && !data.reused) {
      const sent = await sendLink({ clinicId, linkId: data.linkId });
      if (sent.error) {
        console.error("[CK-3876] Auto-send after create failed", { errorCode: "CK-3876", errorMessage: sent.error.message, linkId: data.linkId, timestamp: new Date().toISOString() });
        showError(`דרישת התשלום נוצרה, אך השליחה נכשלה: ${sent.error.message}`);
      } else {
        sentMsg = sent.data?.channel === "whatsapp" ? " ונשלחה בוואטסאפ" : sent.data?.channel === "sms" ? " ונשלחה ב-SMS" : " ונשלחה";
      }
    }
    showSuccess(
      data.reused
        ? copied ? "קיימת כבר דרישת תשלום פעילה — הקישור הועתק" : "קיימת כבר דרישת תשלום פעילה"
        : `דרישת התשלום נוצרה${sentMsg}${copied ? " והקישור הועתק" : ""}`
    );
    setJustCreated(true);
    if (onCreated) onCreated(data);
  };

  // Creating a link is an external, non-silent action (Grow page + clipboard) →
  // deliberate step before it fires from a row/card (design 2026 §9.1).
  const onClick = (e) => {
    if (stopPropagation) e.stopPropagation();
    if (blocker || isLoading) return;
    if (confirm) setConfirming(true);
    else create();
  };

  const stopKeys = (e) => {
    if (stopPropagation) e.stopPropagation();
  };

  const btn = (
    <button
      type="button"
      className={`grow-req-btn ${variant} ${size} ${className}`.trim()}
      onClick={onClick}
      onKeyDown={stopKeys}
      disabled={!!blocker || isLoading || isSending || justCreated}
      aria-disabled={!!blocker}
    >
      {isLoading || isSending ? <LoaderLoop size="xs" /> : <PaperPlaneTilt size={size === "sm" ? 13 : 15} weight="fill" />}
      <span>{label}</span>
    </button>
  );

  const dialog = confirming ? (
    <ConfirmDialog
      isOpen
      onClose={() => setConfirming(false)}
      onConfirm={create}
      title="יצירת דרישת תשלום Grow"
      message={`ליצור דרישת תשלום על ₪${sum.toLocaleString("he-IL", { maximumFractionDigits: 2 })}${patientName ? ` עבור ${patientName}` : ""}? ${sendImmediately ? "הקישור ייווצר ב-Grow ויישלח למטופל בוואטסאפ (או SMS)." : "הקישור ייווצר ב-Grow ויועתק — השליחה למטופל נעשית מפרטי הדרישה."}`}
      confirmText="יצירת דרישת תשלום"
      cancelText="ביטול"
      type="info"
      icon="info"
    />
  ) : null;

  return (
    <>
      {blocker ? (
        <HoverTip text={blocker}>
          <span className="grow-req-wrap" onKeyDown={stopKeys}>{btn}</span>
        </HoverTip>
      ) : (
        btn
      )}
      {dialog}
    </>
  );
}
